import type { FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'

interface PhoneFrameProps {
    children: ReactNode
    orientation?: 'landscape' | 'portrait'
    reflect?: boolean
    className?: string
    screenClassName?: string
}

// Every dimension is expressed in container units of the wrapper (or of the screen
// for its content), so the device scales like a vector illustration at any size.
const SHAPES = {
    landscape: {
        body: 'aspect-[2.16/1] rounded-[11cqw] p-[0.5cqw] shadow-[0_2.2cqw_4.4cqw_-1.4cqw_rgb(2_6_23/0.5),0_0.6cqw_1.2cqw_-0.4cqw_rgb(2_6_23/0.3)]',
        bezel: 'rounded-[10.5cqw] p-[1.55cqw]',
        screen: 'rounded-[8.95cqw]',
        island: 'left-[1.5cqw] top-1/2 h-[10.5cqw] w-[2.1cqw] -translate-y-1/2',
        buttons: [
            '-top-[0.3cqw] left-[22cqw] h-[0.42cqw] w-[5.5cqw] rounded-t-[0.4cqw]',
            '-top-[0.3cqw] left-[29cqw] h-[0.42cqw] w-[5.5cqw] rounded-t-[0.4cqw]',
            '-bottom-[0.3cqw] right-[26cqw] h-[0.42cqw] w-[8cqw] rounded-b-[0.4cqw]'
        ]
    },
    portrait: {
        body: 'aspect-[1/2.16] rounded-[23.8cqw] p-[1.08cqw] shadow-[0_4.4cqw_9cqw_-3cqw_rgb(2_6_23/0.5),0_1.2cqw_2.4cqw_-0.8cqw_rgb(2_6_23/0.3)]',
        bezel: 'rounded-[22.7cqw] p-[3.35cqw]',
        screen: 'rounded-[19.4cqw]',
        island: 'left-1/2 top-[3.2cqw] h-[7.6cqw] w-[26cqw] -translate-x-1/2',
        buttons: [
            '-left-[0.65cqw] top-[22%] h-[11cqw] w-[0.9cqw] rounded-l-[0.6cqw]',
            '-left-[0.65cqw] top-[33%] h-[11cqw] w-[0.9cqw] rounded-l-[0.6cqw]',
            '-right-[0.65cqw] top-[27%] h-[16cqw] w-[0.9cqw] rounded-r-[0.6cqw]'
        ]
    }
}

const PhoneFrame: FC<PhoneFrameProps> = ({
    children,
    orientation = 'landscape',
    reflect = true,
    className,
    screenClassName
}): ReactNode => {
    const shape = SHAPES[orientation]

    return (
        <div className={cn('@container relative w-full', className)} aria-hidden='true'>
            <div
                className={cn(
                    'relative bg-[linear-gradient(145deg,#e5e7eb_0%,#6b7280_30%,#d1d5db_52%,#4b5563_78%,#9ca3af_100%)]',
                    shape.body,
                    reflect && 'phone-reflect'
                )}
            >
                {shape.buttons.map((button) => (
                    <span key={button} className={cn('absolute bg-zinc-400', button)} />
                ))}

                <div className={cn('h-full w-full bg-black', shape.bezel)}>
                    <div className={cn('@container relative isolate h-full w-full overflow-hidden bg-[#05070d]', shape.screen, screenClassName)}>
                        {children}

                        <span className={cn('absolute z-30 rounded-full bg-black ring-1 ring-white/5', shape.island)} />

                        <span className='pointer-events-none absolute inset-0 z-20 bg-[linear-gradient(118deg,rgb(255_255_255/0.1)_0%,rgb(255_255_255/0.03)_28%,transparent_42%)]' />
                    </div>
                </div>
            </div>
        </div>
    )
}

export default PhoneFrame
